import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

const TRIAL_DURATION_DAYS = 14;

export async function checkAndUpdateTrialStatus(uid: string): Promise<boolean> {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    const plan = userData.plan || 'free';
    const trialStartedAt = userData.trialStartedAt;
    const trialExpired = userData.trialExpired || false;

    if (plan !== 'free' || !trialStartedAt || trialExpired) {
      return trialExpired;
    }

    const now = Timestamp.now();
    const trialStart = trialStartedAt instanceof Timestamp 
      ? trialStartedAt 
      : Timestamp.fromDate(new Date(trialStartedAt));

    const daysSinceStart = (now.toMillis() - trialStart.toMillis()) / (1000 * 60 * 60 * 24);

    if (daysSinceStart >= TRIAL_DURATION_DAYS) {
      await updateDoc(userRef, {
        trialExpired: true,
      });
      
      console.log(`[TrialService] Trial expired for user ${uid}`);
      return true;
    }

    return false;
  } catch (error) {
    console.error('[TrialService] Error checking trial status:', error);
    return false;
  }
}

export function getTrialDaysRemaining(trialStartedAt: any): number {
  if (!trialStartedAt) return 0;

  const now = new Date();
  const trialStart = trialStartedAt instanceof Timestamp 
    ? trialStartedAt.toDate() 
    : new Date(trialStartedAt);

  const daysSinceStart = (now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24);
  const daysRemaining = TRIAL_DURATION_DAYS - daysSinceStart;

  return Math.max(0, Math.ceil(daysRemaining));
}

export function isTrialActive(trialStartedAt: any, trialExpired: boolean): boolean {
  if (trialExpired) return false;
  if (!trialStartedAt) return false;

  const daysRemaining = getTrialDaysRemaining(trialStartedAt);
  return daysRemaining > 0;
}

export async function startTrial(uid: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', uid);
    
    await updateDoc(userRef, {
      trialStartedAt: Timestamp.now(),
      trialExpired: false,
    });
    
    console.log(`[TrialService] Trial started for user ${uid}`);
  } catch (error) {
    console.error('[TrialService] Error starting trial:', error);
    throw error;
  }
}
